import React, { useRef } from 'react';
import { cn } from './cn';
import { Icon } from './base/Icon';

/**
 * Tabs (เดสก์ท็อป) — ต่างจาก Tabs ของแอปมือถือที่เพิ่ม "ตัวเลขนับต่อแท็บ"
 * เพราะคิวงานต้องเห็นจำนวนค้างของทุกสถานะพร้อมกันโดยไม่ต้องกดเข้าไปดู
 *
 * เดินด้วยลูกศรซ้าย/ขวา · Home/End ไปแท็บแรก/สุดท้าย
 */
export interface TabItem {
  id: string;
  label: string;
  count?: number;
  icon?: string;
  disabled?: boolean;
  /** ป้ายเพิ่ม เช่น 'Phase 2' */
  note?: string;
}

export interface TabsProps {
  items: TabItem[];
  value: string;
  onChange: (id: string) => void;
  ariaLabel: string;
  className?: string;
}

export const Tabs: React.FC<TabsProps> = ({ items, value, onChange, ariaLabel, className }) => {
  const listRef = useRef<HTMLDivElement>(null);

  const move = (dir: number) => {
    const usable = items.filter((i) => !i.disabled);
    const at = usable.findIndex((i) => i.id === value);
    const next = usable[(at + dir + usable.length) % usable.length];
    if (next) onChange(next.id);
  };

  return (
    <div
      ref={listRef}
      role="tablist"
      aria-label={ariaLabel}
      onKeyDown={(e) => {
        if (e.key === 'ArrowRight') { e.preventDefault(); move(1); }
        if (e.key === 'ArrowLeft') { e.preventDefault(); move(-1); }
        if (e.key === 'Home') { e.preventDefault(); onChange(items.find((i) => !i.disabled)!.id); }
        if (e.key === 'End') { e.preventDefault(); onChange([...items].reverse().find((i) => !i.disabled)!.id); }
      }}
      className={cn('flex items-end gap-[var(--space-1)] overflow-x-auto border-b border-[var(--line-200)] font-[family-name:var(--font-thai)]', className)}
    >
      {items.map((t) => {
        const active = t.id === value;
        return (
          <button
            key={t.id}
            role="tab"
            type="button"
            aria-selected={active}
            aria-controls={`panel-${t.id}`}
            tabIndex={active ? 0 : -1}
            disabled={t.disabled}
            onClick={() => onChange(t.id)}
            className={cn(
              'inline-flex min-h-[var(--hit-primary)] shrink-0 items-center gap-[var(--space-15)] border-b-2 px-[var(--space-3)] text-[length:var(--text-form-lg)] leading-[var(--leading-form)] outline-none focus-visible:ring-[3px] focus-visible:ring-[var(--focus-ring-color)] focus-visible:ring-offset-1',
              active
                ? 'border-[var(--blue-700)] font-[var(--weight-semibold)] text-[var(--blue-700)]'
                : 'border-transparent font-[var(--weight-regular)] text-[var(--ink-700)] hover:border-[var(--line-300)] hover:text-[var(--ink-900)]',
              t.disabled && 'cursor-not-allowed text-[var(--text-disabled)] hover:border-transparent',
            )}
          >
            {t.icon && <Icon name={t.icon} size={18} filled={active} />}
            {t.label}
            {t.count !== undefined && (
              <span
                className={cn(
                  'bo-num rounded-[var(--radius-pill)] px-[var(--space-15)] text-[length:var(--text-table-sm)] font-[var(--weight-semibold)]',
                  active ? 'bg-[var(--blue-700)] text-white' : 'bg-[var(--surface-100)] text-[var(--ink-700)]',
                )}
              >
                {t.count.toLocaleString('th-TH')}
              </span>
            )}
            {t.note && (
              <span className="rounded-[var(--radius-sm)] bg-[var(--warning-bg)] px-1 text-[length:var(--text-table-sm)] text-[var(--warning-text)]">
                {t.note}
              </span>
            )}
          </button>
        );
      })}
    </div>
  );
};
